import { Center, Loader, MantineProvider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Grid, RpiAlert } from "@rpi/openapi-core";
import React from "react";
import { Outlet } from "react-router-dom";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { rpaTheme } from "../../../theme/rpaTheme";
import { AccountInformationCard } from "./AccountInformationCard";
import { OrderCard } from "./OrderCard";
import { OrganizationInformationCard } from "./OrganizationInformationCard";
import { PaymentMethodCard } from "./PaymentMethodCard";
import { canEditAnyOrganizationField } from "./organizationFields";

export const SettingsViewV2: React.FC = () => {
  const { classes } = usePageShellStyles();
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  // Match useAuthUser's staleTime so this shared query isn't refetched on mount.
  const { data, isLoading, error } = GraphQL.useDashboardUserCurrentQuery(undefined, { staleTime: Infinity });
  const dashboardUser = data?.dashboardUserCurrent;

  const isCustomerAdmin = user.is.CUSTOMER_ADMIN;
  const ordersLimit = dashboardUser?.customer?.settings?.ordersLimit;

  const handleEditOrganization = () => navigateRoute(["settings:edit-org"]);
  const handleUploadTaxExempt = () => navigateRoute(["settings:tax-exempt-upload"]);

  const renderBody = () => {
    if (error) return <RpiAlert>We were unable to load your account settings. Please try again.</RpiAlert>;
    if (isLoading || !dashboardUser)
      return (
        <Center sx={{ flex: 1 }}>
          <Loader size="xl" />
        </Center>
      );

    const organization = dashboardUser.customer?.organization;

    return (
      <Grid
        columns="repeat(2, minmax(0, 1fr))"
        gap={24}
        sx={{ "@media (max-width: 62em)": { gridTemplateColumns: "1fr" } }}
      >
        <AccountInformationCard dashboardUser={dashboardUser} onEdit={() => navigateRoute(["settings:edit-account"])} />
        <OrganizationInformationCard
          organizationName={dashboardUser.customer?.organizationName}
          website={organization?.website}
          size={organization?.size}
          country={organization?.country}
          canEdit={canEditAnyOrganizationField(organization, dashboardUser.role || undefined)}
          onEdit={handleEditOrganization}
        />
        {isCustomerAdmin && user.id && <PaymentMethodCard customerPublicId={user.id} />}
        <OrderCard
          maxOrders={ordersLimit?.maxOrders}
          limitQueueSize={ordersLimit?.limitQueueSize}
          showTaxExemption={isCustomerAdmin}
          onUploadTaxExempt={handleUploadTaxExempt}
        />
      </Grid>
    );
  };

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="account-settings-view" aria-label="Account settings view">
        <Heading as="h1" size="6xl">
          Account Settings
        </Heading>
        <Outlet />
        {renderBody()}
      </div>
    </MantineProvider>
  );
};
